import {isSpan} from '@portabletext/schema'
import {end} from '../engine/editor/end'
import {isEdge} from '../engine/editor/is-edge'
import {isEnd} from '../engine/editor/is-end'
import {pathRef} from '../engine/editor/path-ref'
import {withoutNormalizing} from '../engine/editor/without-normalizing'
import type {Node} from '../engine/interfaces/node'
import type {Path} from '../engine/interfaces/path'
import type {Point} from '../engine/interfaces/point'
import {getNode} from '../traversal/get-node'
import {isObject} from '../traversal/is-object'
import type {PortableTextEditorEngine} from '../types/editor-engine'
import {applySelect} from './apply-selection'
import {applySplitNode} from './apply-split-node'

/**
 * Insert a node at a known path and optionally select the end of the
 * inserted node.
 */
export function applyInsertNodeAtPath(
  editor: PortableTextEditorEngine,
  node: Node,
  path: Path,
): void {
  editor.apply({type: 'insert', path, node, position: 'before'})

  const point = end(editor, path)

  if (point) {
    applySelect(editor, point)
  }
}

/**
 * Insert a node at a Point, splitting the existing text node if needed,
 * and optionally select the end of the inserted node.
 */
export function applyInsertNodeAtPoint(
  editor: PortableTextEditorEngine,
  node: Node,
  at: Point,
): void {
  withoutNormalizing(editor, () => {
    const match = isSpan({schema: editor.snapshot.context.schema}, node)
      ? (n: Node) => isSpan({schema: editor.snapshot.context.schema}, n)
      : (n: Node) =>
          isSpan({schema: editor.snapshot.context.schema}, n) ||
          isObject(editor.snapshot, n)

    const nodeEntry = getNode(editor.snapshot, at.path)
    const entry = nodeEntry && match(nodeEntry.node) ? nodeEntry : undefined

    if (!entry) {
      return
    }

    const matchPath = entry.path
    const ref = pathRef(editor, matchPath)
    const isAtEnd = isEnd(editor, at, matchPath)

    // Split the node at the point if we're not at an edge
    if (!isEdge(editor, at, matchPath)) {
      applySplitNode(editor, at.path, at.offset)
    }

    const path = ref.unref()!

    editor.apply({
      type: 'insert',
      path,
      node,
      position: isAtEnd ? 'after' : 'before',
    })

    const parentSegments = path.slice(0, -1)
    const newNodePath: Path = node._key
      ? [...parentSegments, {_key: node._key}]
      : path
    const point = end(editor, newNodePath)
    if (point) {
      applySelect(editor, point)
    }
  })
}
